// DataSheetCell.jsx
import React, { useState, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import { useTheme } from "@mui/material/styles";

// Import the existing components from your data sheet dialog
// Adjust these imports based on your actual file structure
import { CategorizedDataSheetContent } from "../PnID/DataSheets/DataSheetsDialog";
import { consolidateDataSheets } from "../PnID/DataSheets/dataSheetHelpers";

const DataSheetCell = ({ row, dataSheetsStore }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Extract and consolidate data sheets that match the current row
  const matchingDataSheets = useMemo(() => {
    if (!dataSheetsStore || !row.original) return null;

    const currentRow = row.original;
    const matchingItems = [];

    // Check valve data
    if (dataSheetsStore.valve?.items) {
      dataSheetsStore.valve.items.forEach((item) => {
        // Check if this item matches the current row
        const matches = item.rawData?.some((raw) => {
          // Match by Tag Number
          const matchesTag =
            raw.TagNumber &&
            currentRow.TagNumber &&
            raw.TagNumber === currentRow.TagNumber;

          // Match by Spec + Component + Size
          const matchesSpec =
            raw.Spec && currentRow.Spec && raw.Spec === currentRow.Spec;
          const matchesComponent =
            raw.Component &&
            currentRow.Components &&
            raw.Component.toLowerCase() === currentRow.Components.toLowerCase();
          const matchesSize =
            raw.Size && currentRow.Size && raw.Size === currentRow.Size;

          return matchesTag || (matchesSpec && matchesComponent && matchesSize);
        });

        if (matches) {
          matchingItems.push(item);
        }
      });
    }

    // Check instrument data
    if (dataSheetsStore.instrument?.items) {
      dataSheetsStore.instrument.items.forEach((item) => {
        const matches = item.rawData?.some((raw) => {
          // Match by Tag Number
          const matchesTag =
            raw["Tag Number"] &&
            currentRow.TagNumber &&
            raw["Tag Number"] === currentRow.TagNumber;

          // Match by Spec + Component
          const matchesSpec =
            raw.Spec && currentRow.Spec && raw.Spec === currentRow.Spec;
          const matchesComponent =
            raw.Component &&
            currentRow.Components &&
            raw.Component.toLowerCase() === currentRow.Components.toLowerCase();

          return matchesTag || (matchesSpec && matchesComponent);
        });

        if (matches) {
          matchingItems.push(item);
        }
      });
    }

    if (matchingItems.length === 0) return null;

    // Return the data in the format expected by DataSheetsDialog
    return {
      tab: matchingItems[0]?.rawData?.[0]?.Component ? "valve" : "instrument",
      items: matchingItems,
      totalSourceRows: matchingItems.reduce(
        (sum, item) => sum + (item.count || 0),
        0,
      ),
      totalGroupedSheets: matchingItems.length,
    };
  }, [dataSheetsStore, row.original]);

  // Reset selected index when matching data sheets change
  React.useEffect(() => {
    setSelectedIndex(0);
  }, [matchingDataSheets]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Early return if no matching data sheets
  if (!matchingDataSheets || matchingDataSheets.items?.length === 0) {
    return (
      <Button
        size="small"
        disabled
        startIcon={<DescriptionIcon />}
        sx={{ minWidth: "auto", p: 0.5, opacity: 0.5 }}
        title="No matching data sheets found"
      />
    );
  }

  const currentItem = matchingDataSheets.items[selectedIndex];

  const getTabDisplayName = (tabKey) => {
    switch (tabKey) {
      case "valve":
        return "Valve List";
      case "instrument":
        return "Instrument Index";
      default:
        return "Data Sheet";
    }
  };

  return (
    <>
      <Button
        size="small"
        onClick={handleOpen}
        startIcon={<DescriptionIcon />}
        sx={{ minWidth: "auto", p: 0.5 }}
        title={`View ${matchingDataSheets.totalGroupedSheets} matching data sheet(s)`}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${theme.palette.primary.main}55`,
            maxWidth: 600,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.25,
            px: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                {getTabDisplayName(matchingDataSheets.tab)} — Data Sheets
              </Typography>
              {currentItem && (
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: theme.palette.text.secondary,
                  }}
                >
                  {currentItem.tagNumbers?.length > 0
                    ? `Tags: ${currentItem.tagNumbers.join(", ")}`
                    : `Tag: ${row.original.TagNumber || "N/A"}`}
                  {" · "}
                  {currentItem.count} row{currentItem.count !== 1 ? "s" : ""}
                </Typography>
              )}
            </Box>
          </Box>

          {matchingDataSheets.items.length > 1 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{ fontSize: "0.7rem", color: theme.palette.text.secondary }}
              >
                {selectedIndex + 1} / {matchingDataSheets.items.length}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={selectedIndex === 0}
                onClick={() => setSelectedIndex((i) => i - 1)}
                sx={{
                  minWidth: 28,
                  px: 0.5,
                  fontSize: "0.7rem",
                  borderColor: `${theme.palette.primary.main}55`,
                  color: theme.palette.primary.main,
                }}
              >
                ‹
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={selectedIndex === matchingDataSheets.items.length - 1}
                onClick={() => setSelectedIndex((i) => i + 1)}
                sx={{
                  minWidth: 28,
                  px: 0.5,
                  fontSize: "0.7rem",
                  borderColor: `${theme.palette.primary.main}55`,
                  color: theme.palette.primary.main,
                }}
              >
                ›
              </Button>
            </Box>
          )}
        </DialogTitle>

        <DialogContent
          sx={{ p: 0, backgroundColor: theme.palette.background.default }}
        >
          {!currentItem ? (
            <Box sx={{ p: 3 }}>
              <Typography
                sx={{ fontSize: "0.8rem", color: theme.palette.text.secondary }}
              >
                No data sheets found for this component.
              </Typography>
            </Box>
          ) : (
            <CategorizedDataSheetContent
              categorizedFields={currentItem.categorizedFields}
            />
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            size="small"
            sx={{
              borderColor: `${theme.palette.primary.main}55`,
              color: theme.palette.primary.main,
              fontSize: "0.72rem",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: `${theme.palette.primary.main}18`,
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DataSheetCell;
